import React, { useState, useEffect } from 'react';
import API from '../services/api';
import { Coffee, Heart, Zap } from 'lucide-react';
import Toast from '../components/Toast';
import faviconImage from '../assets/favicon.png';

const AMOUNTS = [
  { value: 29,  label: '☕ Small Coffee', icon: Coffee, desc: 'A small gesture of support', color: '#f59e0b' },
  { value: 49,  label: '☕☕ Large Coffee', icon: Coffee, desc: 'Keep the servers running', color: '#10b981' },
  { value: 99,  label: '⚡ Power Boost',  icon: Zap,    desc: 'You\'re a true champion!', color: '#8b5cf6' },
];

export default function DonatePage() {
  const [selected, setSelected] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [message, setMessage]   = useState('');
  const [donors, setDonors]     = useState([]);
  const [loading, setLoading]   = useState(false);
  const [toast, setToast]       = useState(null);
  const [donationsEnabled, setDonationsEnabled] = useState(true);

  useEffect(() => {
    API.get('/donations/recent').then(r => {
      setDonationsEnabled(r.data.enabled);
      setDonors(r.data.donors || []);
    }).catch(console.error);
  }, []);

  const handleDonate = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const r = await API.post('/donations/create', {
        amount: selected,
        display_name: displayName.trim() || 'Anonymous',
        message: message.trim(),
      });

      const { order_id, amount, currency, key, donation_id } = r.data;

      // Load Razorpay script
      await new Promise((resolve, reject) => {
        if (window.Razorpay) return resolve();
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });

      const rzp = new window.Razorpay({
        key,
        amount,
        currency,
        order_id,
        name: 'IPLTrade',
        description: `Donation — ₹${selected} Coffee`,
        image: window.location.origin + faviconImage,
        handler: async (response) => {
          try {
            await API.post('/donations/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              donation_id,
            });
            setToast({ msg: 'Thank you for your support! ❤️', type: 'success' });
            setSelected(null);
            setDisplayName('');
            setMessage('');
            // Refresh donors
            const dr = await API.get('/donations/recent');
            setDonors(dr.data.donors || []);
          } catch (err) {
            setToast({ msg: 'Payment recorded but verification pending', type: 'warning' });
          }
        },
        prefill: { name: displayName },
        theme: { color: '#f59e0b' },
        modal: {
          ondismiss: () => setLoading(false),
        },
      });

      rzp.open();
    } catch (err) {
      setToast({ msg: err.response?.data?.error || 'Payment setup failed', type: 'error' });
      setLoading(false);
    }
  };

  if (!donationsEnabled) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
        <Coffee size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
        <p>Donations are not currently enabled.</p>
      </div>
    );
  }

  const timeAgo = (ts) => {
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      {toast && <Toast message={toast.msg} type={toast.type} onDismiss={() => setToast(null)} />}

      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Support IPLTrade</h1>
        <p className="page-subtitle">Keep the platform free for everyone</p>
      </div>

      {/* Disclaimer */}
      <div style={{
        background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.2)',
        borderRadius: 10, padding: '12px 14px', marginBottom: '1.5rem',
        fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.6,
      }}>
        <strong style={{ color: 'var(--green)' }}>Pure donation — no benefits, no coins, no advantages.</strong>
        {' '}This is entirely optional support to help cover server costs and keep IPLTrade free for all players.
        Donating does NOT give you any in-game benefits, extra points, or advantages of any kind.
      </div>

      {/* Amount selection */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: '1.25rem' }}>
        {AMOUNTS.map(a => (
          <button
            key={a.value}
            onClick={() => setSelected(selected === a.value ? null : a.value)}
            style={{
              background: selected === a.value ? a.color + '20' : 'var(--bg-card)',
              border: `2px solid ${selected === a.value ? a.color : 'var(--border)'}`,
              borderRadius: 12, padding: '14px 10px', cursor: 'pointer',
              transition: 'all 0.15s', textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '1.4rem', marginBottom: 4 }}>
              {a.value === 29 ? '☕' : a.value === 49 ? '☕' : '⚡'}
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 700, color: selected === a.value ? a.color : 'var(--text-primary)' }}>
              ₹{a.value}
            </div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 3 }}>{a.desc}</div>
          </button>
        ))}
      </div>

      {selected && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '1rem', marginBottom: '1rem' }}>
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label className="form-label">Display name (shown publicly)</label>
            <input
              className="form-input"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="Anonymous"
              maxLength={50}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Message (optional)</label>
            <input
              className="form-input"
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Love the platform! 🏏"
              maxLength={100}
            />
          </div>
        </div>
      )}

      <button
        className="btn btn-buy"
        style={{ width: '100%', padding: '12px', fontSize: '0.95rem', fontFamily: 'var(--font-display)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: selected ? 1 : 0.5, marginBottom: '2rem' }}
        onClick={handleDonate}
        disabled={!selected || loading}
      >
        <Heart size={16} />
        {loading ? 'Opening payment...' : selected ? `Donate ₹${selected} — Buy a Coffee` : 'Select an amount above'}
      </button>

      {/* Donor wall */}
      {donors.length > 0 && (
        <div>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--text-secondary)' }}>
            Recent supporters ❤️
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {donors.map((d, i) => (
              <div key={i} style={{
                background: 'var(--bg-card)', border: '1px solid var(--border)',
                borderRadius: 10, padding: '10px 14px',
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                  background: 'rgba(245,158,11,0.15)', color: 'var(--accent)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.85rem',
                }}>
                  {(d.display_name || 'A')[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{d.display_name || 'Anonymous'}</div>
                  {d.message && <div style={{ fontSize: '0.73rem', color: 'var(--text-secondary)', marginTop: 1 }}>{d.message}</div>}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--accent)' }}>₹{d.amount}</div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{timeAgo(d.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
